import React, { useCallback, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";

import MonthDropdown from "../../../components/inputs/MonthDropdown";
import YearDropdown from "../../../components/inputs/YearDropdown";
import { InputLabel } from "../../../components/inputs/InputLabel";

import { generateCreditNotesForAllCustomers } from "../../../services/clearing/credit-notes";

const today = new Date();
const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);

const initialSelectedPeriod = {
  month: lastMonth.getMonth() + 1,
  year: lastMonth.getFullYear(),
};

interface GenerateAllCreditNotesDialogProps {
  visible: boolean;
  onClose: () => void;
  toastRef: React.RefObject<any>;
  onGenerated?: () => void;
  onSubmitted?: () => void;
}

export function GenerateAllCreditNotesDialog({ visible, onClose, toastRef, onGenerated, onSubmitted }: GenerateAllCreditNotesDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(initialSelectedPeriod);

  const onGenerate = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const res = await generateCreditNotesForAllCustomers({
        year: selectedPeriod.year,
        month: selectedPeriod.month,
      });

      if (res.status === "already_running") {
        toastRef.current?.show({
          severity: "warn",
          summary: "Already Running",
          detail: "A credit-note generation is already in progress.",
          life: 5000,
        });
      } else if (res.status === "error") {
        toastRef.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to start credit note generation.",
          life: 3000,
        });
      } else {
        // status === "started" — only close the dialog on a successful submit.
        // On already_running / error we keep it open so the user can retry
        // without re-selecting the period (the page-level toast still shows).
        toastRef.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Credit note generation started.",
          life: 3000,
        });
        onSubmitted?.();
        onClose();
        onGenerated?.();
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedPeriod, onClose, onGenerated, onSubmitted, toastRef, isLoading]);

  return (
    <Dialog
      header="Generate All Credit Notes"
      visible={visible}
      style={{ width: "35vw" }}
      onHide={onClose}
    >
      <section className="flex flex-column">
        <div>
          <InputLabel label="Period" required />
          <div className="flex flex-column sm:flex-row gap-2 field">
            <MonthDropdown
              monthIndex={selectedPeriod.month}
              onChange={(option) =>
                setSelectedPeriod((prev) => ({
                  ...prev,
                  month: Number(option.code),
                }))
              }
            />
            <YearDropdown
              year={selectedPeriod.year}
              onChange={(option) =>
                setSelectedPeriod((prev) => ({
                  ...prev,
                  year: Number(option.code),
                }))
              }
            />
          </div>
        </div>

        <Button
          className="w-full sm:w-16rem sm:h-3rem sm:mt-2"
          style={{ alignSelf: "end" }}
          label="Generate All Credit Notes"
          icon="pi pi-receipt"
          onClick={onGenerate}
          loading={isLoading}
          disabled={isLoading}
        />
      </section>
    </Dialog>
  );
}
